/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v3";
import { remap as remap$ } from "../../lib/primitives.js";
import { safeParse } from "../../lib/schemas.js";
import { Result as SafeParseResult } from "../../types/fp.js";
import * as types from "../../types/primitives.js";
import { SDKValidationError } from "../errors/sdkvalidationerror.js";
import {
  AnimaticGaugeConfig,
  AnimaticGaugeConfig$inboundSchema,
  AnimaticGaugeConfig$Outbound,
  AnimaticGaugeConfig$outboundSchema,
} from "./animaticgaugeconfig.js";
import {
  ConditionExpression,
  ConditionExpression$inboundSchema,
  ConditionExpression$Outbound,
  ConditionExpression$outboundSchema,
} from "./conditionexpression.js";

/**
 * Defines a specific animatic gauge state which is conditionally applied based on the condition.
 */
export type AnimaticGaugeState = {
  /**
   * Defines a condition expression. Can be a single condition, a group of conditions, or nested groups. Exactly one of 'condition', 'condition_group', or 'nested_expression' must be specified.
   */
  conditionExpression?: ConditionExpression | undefined;
  /**
   * Defines an animatic gauge that is overlaid on top of an animatic, its states, and how it should be configured for each state.
   */
  config: AnimaticGaugeConfig;
  /**
   * Whether this state is the default state. If this is true, conditions are not required. For readability, these states should be at the end of the array.
   */
  isDefault?: boolean | undefined;
  /**
   * The priority of the state. There is no constraint on overlapping conditions, so this field allows user to set priority.
   */
  priority?: number | undefined;
  /**
   * The name of the state
   */
  stateName: string;
};

/** @internal */
export const AnimaticGaugeState$inboundSchema: z.ZodType<
  AnimaticGaugeState,
  z.ZodTypeDef,
  unknown
> = z.object({
  condition_expression: types.optional(ConditionExpression$inboundSchema),
  config: AnimaticGaugeConfig$inboundSchema,
  is_default: types.boolean().default(false),
  priority: types.number().default(0),
  state_name: types.string(),
}).transform((v) => {
  return remap$(v, {
    "condition_expression": "conditionExpression",
    "is_default": "isDefault",
    "state_name": "stateName",
  });
});
/** @internal */
export type AnimaticGaugeState$Outbound = {
  condition_expression?: ConditionExpression$Outbound | undefined;
  config: AnimaticGaugeConfig$Outbound;
  is_default: boolean;
  priority: number;
  state_name: string;
};

/** @internal */
export const AnimaticGaugeState$outboundSchema: z.ZodType<
  AnimaticGaugeState$Outbound,
  z.ZodTypeDef,
  AnimaticGaugeState
> = z.object({
  conditionExpression: ConditionExpression$outboundSchema.optional(),
  config: AnimaticGaugeConfig$outboundSchema,
  isDefault: z.boolean().default(false),
  priority: z.number().int().default(0),
  stateName: z.string(),
}).transform((v) => {
  return remap$(v, {
    conditionExpression: "condition_expression",
    isDefault: "is_default",
    stateName: "state_name",
  });
});

export function animaticGaugeStateToJSON(
  animaticGaugeState: AnimaticGaugeState,
): string {
  return JSON.stringify(
    AnimaticGaugeState$outboundSchema.parse(animaticGaugeState),
  );
}
export function animaticGaugeStateFromJSON(
  jsonString: string,
): SafeParseResult<AnimaticGaugeState, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => AnimaticGaugeState$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'AnimaticGaugeState' from JSON`,
  );
}
